import type {
    CreateExtArgs,
    CreateExtensionArgument,
    DmApis,
    Extension,
    ExtensionAPI,
    ExtensionPublicAPI
} from '@wix/document-manager-core'
import type {CompRef, CompStructure, Pointer, SerializedCompStructure} from '@wix/document-services-types'
import _ from 'lodash'
import type {ComponentsExtensionAPI} from '../components/components'
import type {DataModelExtensionAPI} from '../dataModel/dataModel'
import type {RelationshipsAPI} from '../relationships'
import {DATA_TYPES, VIEW_MODES} from '../../constants/constants'
import type {StructureExtensionAPI} from '../structure'

export interface GlobalComponentsAPI {
    getReferringComponents(globalCompPointer: CompRef): CompRef[]
    getGlobalComponentToAddRef(): CompRef
    add(componentToAddRef: CompRef, structure: CompStructure): void
    remove(globalCompPointer: CompRef): void
    isGlobalComponent(compPointer: CompRef): boolean
}

export type GlobalComponentsExtensionAPI = ExtensionAPI & {
    globalComponents: GlobalComponentsAPI
}

const createExtension = ({}: CreateExtensionArgument): Extension => {
    const comparePointers = (p1: Pointer, p2: Pointer) => p1.type === p2.type && p1.id === p2.id

    const createExtensionAPI = ({extensionAPI, pointers, dal}: CreateExtArgs): GlobalComponentsExtensionAPI => {
        const {relationships} = extensionAPI as RelationshipsAPI
        const {dataModel} = extensionAPI as DataModelExtensionAPI
        const {components} = extensionAPI as ComponentsExtensionAPI
        const {components: structureComponents} = extensionAPI as StructureExtensionAPI

        const getReferringComponents = (globalCompPointer: CompRef): CompRef[] => {
            if (!pointers.structure.isInMasterPage(globalCompPointer)) {
                throw new Error('The component is not a global component')
            }
            const refsToGlobal = relationships.getAllReferencesToPointer(globalCompPointer)
            const componentRefs = _(refsToGlobal)
                .flatMap(p => relationships.getComponentsEventuallyReferencingItem(p))
                .uniqWith(comparePointers)
                .value() as CompRef[]

            return componentRefs.filter(compPointer => {
                const componentStructureType = structureComponents.getType(compPointer)
                if (componentStructureType !== 'RefComponent') {
                    return false
                }
                const dataItem = dataModel.components.getItem(compPointer, DATA_TYPES.data)
                return dataItem?.rootCompId === globalCompPointer.id
            })
        }

        const getGlobalComponentToAddRef = (): CompRef => components.getComponentToAddRef(VIEW_MODES.DESKTOP)

        const add = (componentToAddRef: CompRef, structure: CompStructure): void => {
            const masterPagePointer = pointers.structure.getMasterPage(VIEW_MODES.DESKTOP) as CompRef
            components.addComponentV2(componentToAddRef, masterPagePointer, structure as SerializedCompStructure)
        }

        const remove = (globalCompPointer: CompRef): void => {
            if (!pointers.structure.isInMasterPage(globalCompPointer)) {
                throw new Error(
                    'Component is not a global component. Only global components can be removed via ds.global.components.remove.'
                )
            }
            const referringComps = getReferringComponents(globalCompPointer)
            _.forEach(referringComps, (refComp: CompRef) => {
                components.deleteComponent(refComp)
            })
            components.deleteComponent(globalCompPointer)
        }

        const isGlobalComponent = (compPointer: CompRef): boolean =>
            dal.has(compPointer) && pointers.structure.isInMasterPage(compPointer)

        return {
            globalComponents: {
                getReferringComponents,
                getGlobalComponentToAddRef,
                add,
                remove,
                isGlobalComponent
            }
        }
    }

    const createPublicAPI = ({extensionAPI}: DmApis): ExtensionPublicAPI => {
        const {globalComponents} = extensionAPI as GlobalComponentsExtensionAPI
        return {
            globalComponents: {
                getReferringComponents(globalCompPointer: CompRef): CompRef[] {
                    return globalComponents.getReferringComponents(globalCompPointer)
                },
                getGlobalComponentToAddRef(): CompRef {
                    return globalComponents.getGlobalComponentToAddRef()
                },
                add(componentToAddRef: CompRef, structure: CompStructure): void {
                    globalComponents.add(componentToAddRef, structure)
                },
                remove(globalCompPointer: CompRef): void {
                    globalComponents.remove(globalCompPointer)
                }
            }
        }
    }

    return {
        name: 'globalComponents',
        dependencies: new Set(['structure', 'relationships', 'dataModel', 'components']),
        createExtensionAPI,
        createPublicAPI
    }
}

export {createExtension}
